import { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { Widget, WidgetEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Add as AddIcon } from '@factorialco/f0-react/icons/app'
import { EventsHomeWidgetDocument, EventsHomeWidgetQuery } from 'generated/resources/graphql'
import { compact } from 'lodash'
import sortBy from 'lodash/sortBy'

import CalendarEventList from 'modules/core/components/CalendarEventList'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { NEW_HOME_WIDGETS, NEW_HOME_WIDGET_ACTION_TYPES } from 'modules/dashboard/lib/events'

const useLinkAndTitle = () => {
  const title = i18n.t('new_dashboard.events.title')

  const navigate = useNavigate()

  const { navigation } = useNavigation()

  const linkUrl = navigation.calendar.fullPath()

  const handleActionClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.EVENTS,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.BOTTOM_BUTTON,
    })
    navigate(linkUrl)
  }

  const handleUpperRightActionClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.EVENTS,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.UPPER_RIGHT_BUTTON,
    })
  }

  return {
    title,
    link: {
      url: linkUrl,
      title,
      onClick: handleUpperRightActionClick,
    },
    handleActionClick,
  }
}

const prepareData = (data: EventsHomeWidgetQuery) => {
  const postsAndTrainingSessions = data.calendar.calendarEventsConnection.nodes.map((event) => {
    if (event.trainingSession) {
      return { ...event.trainingSession, date: event.trainingSession.startsAt }
    }

    if (event.post) {
      return { ...event.post, date: event.post.startsAt }
    }
  })

  const shiftsAndLeaves = data.calendar.shiftsAndLeaves.nodes.map((event) => {
    if (event.shiftManagement) {
      return { ...event.shiftManagement, date: event.shiftManagement.localStartAt }
    }

    if (event.leave) {
      return { ...event.leave, date: event.leave.startOn }
    }
  })

  const holidays = data.holidays.companyHolidaysConnection.nodes || []

  const list = sortBy(compact([...postsAndTrainingSessions, ...shiftsAndLeaves, ...holidays]), [
    (event) => event.date,
  ]).slice(0, 10)

  const buttonLabel = i18n.t('new_dashboard.events.see_more')

  return {
    list,
    buttonLabel,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const EventsWidgetComponent: FC<{ data: PreparedData; employeeId: number }> = ({
  data: { list, buttonLabel },
}) => {
  const { link, title, handleActionClick } = useLinkAndTitle()

  return (
    <Widget
      header={{
        title,
        link,
      }}
      action={{ label: buttonLabel, onClick: handleActionClick }}
    >
      <CalendarEventList items={list} />
    </Widget>
  )
}

const EmptyComponent = () => {
  const { link, title } = useLinkAndTitle()

  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const handleClickAddEvent = () => {
    navigate(navigation.dashboard.post.newEvent.fullPath())
  }

  return (
    <Widget header={{ title, link }}>
      <WidgetEmptyState
        title={i18n.t('new_dashboard.events.empty.title')}
        description={i18n.t('new_dashboard.events.empty.description')}
        emoji="🗓️"
        actions={[
          {
            label: i18n.t('new_dashboard.events.empty.add_event'),
            icon: AddIcon,
            variant: 'outline',
            onClick: handleClickAddEvent,
          },
        ]}
      />
    </Widget>
  )
}

export const buildEventsWidget = () =>
  buildWidget({
    id: 'events',
    document: EventsHomeWidgetDocument,
    emptyComponent: () => <EmptyComponent />,
    isEmpty: ({ list }) => !list.length,
    prepareData,
    component: EventsWidgetComponent,
  })
